import React from 'react';
import { ChangeMeta, NumberFormatBase, NumberFormatValues, SourceInfo, usePatternFormat } from 'react-number-format';
import Input from '../Input/Input';
import {
  PHONE_FORMAT,
  PHONE_MASK_CHAR,
  PHONE_PLACEHOLDER,
  isCompletePhone,
  isPastedFullNumber,
  nationalDigits,
  toStoredPhone,
} from '../../utils/phone';
import './PhoneInput.css';

interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type' | 'defaultValue'> {
  id: string;
  label?: string;
  editable?: boolean;
  // value — «7XXXXXXXXXX» или пустая строка; isValid — введены все 10 цифр
  onChangeValue?: (value: string, isValid: boolean) => void;
  value?: string;
  customInput?: boolean; // Флаг для использования кастомного инпута вместо компонента Input
  className?: string; // Дополнительные CSS классы
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  onChangeValue,
  value = '',
  customInput = false,
  className = '',
  ...inputProps
}) => {
  // Сообщаем только о вводе пользователя, не о смене value сверху
  const handleValueChange = ({ value: national }: NumberFormatValues, { source }: SourceInfo) => {
    if (source !== 'event') return;
    onChangeValue?.(toStoredPhone(national), isCompletePhone(national));
  };

  const { removeFormatting = nationalDigits, ...maskProps } = usePatternFormat({
    format: PHONE_FORMAT,
    mask: PHONE_MASK_CHAR,
    value: nationalDigits(value),
    valueIsNumericString: true,
    onValueChange: handleValueChange,
    type: 'tel',
  });

  // Маска сама читает набранное; полный номер с кодом страны, вставленный целиком, она приняла бы за 10 цифр номера
  const removePhoneFormatting = (input: string, changeMeta?: ChangeMeta) =>
    isPastedFullNumber(input) ? nationalDigits(input) : removeFormatting(input, changeMeta);

  // Если запрошен кастомный инпут, используем обычный input с нашими стилями
  if (customInput) {
    return (
      <NumberFormatBase
        placeholder={PHONE_PLACEHOLDER}
        {...inputProps}
        {...maskProps}
        removeFormatting={removePhoneFormatting}
        className={`phoneInput  ${className}`}
      />
    );
  }

  // В противном случае используем компонент Input
  return (
    <NumberFormatBase
      {...inputProps}
      {...maskProps}
      removeFormatting={removePhoneFormatting}
      placeholder={PHONE_PLACEHOLDER}
      customInput={Input}
    />
  );
};

export default PhoneInput;
